"use client";

import {
  ChartBarIcon,
  CoinsIcon,
  HistoryIcon,
  ShieldCheckIcon,
  ShoppingCartIcon,
  UsersIcon,
} from "lucide-react";
import { memo, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { promptGenerators } from "@/hooks/use-quick-action";
import { cn } from "@/lib/utils";
import { AddressDisplay } from "./address-display";
import { ChainBadge } from "./chain-badge";
import { CollapsibleCard } from "./collapsible-card";
import { QuickActionButton } from "./quick-action-button";
import { QuickActionsBar } from "./quick-actions-bar";
import type {
  QuickAction,
  TokenCardProps,
  TokenHolder,
  TokenInfo,
} from "./types";
import { getChainInfo } from "./types";
import { formatLargeNumber, formatUsdValue } from "./value-display";

/**
 * Default number of holders to show
 */
const DEFAULT_VISIBLE_HOLDERS = 5;

/**
 * Main TokenCard component - displays token information with holders
 */
const PureTokenCard = ({
  cardId,
  input,
  output,
  state,
  onQuickAction,
  isReadonly,
}: TokenCardProps) => {
  const _chain = getChainInfo(input.chainId);

  // Handle loading states
  if (state === "input-streaming" || state === "input-available") {
    return <TokenCardSkeleton chainId={input.chainId} tokens={input.tokens} />;
  }

  // Handle error state - check if it's a plain error object (not a token result with error)
  // A plain error has `error` directly on the output, not nested in token results
  if (output && "error" in output && !("address" in output)) {
    const errorOutput = output as { error: string; suggestions?: string[] };
    return (
      <TokenCardError
        chainId={input.chainId}
        error={errorOutput.error}
        tokens={input.tokens}
      />
    );
  }

  // Handle empty output
  if (!output || Object.keys(output).length === 0) {
    return (
      <TokenCardError
        chainId={input.chainId}
        error="No token data found"
        tokens={input.tokens}
      />
    );
  }

  // At this point, output must be Record<string, TokenInfo & ...>
  // TypeScript needs help narrowing this
  const tokenOutput = output as Record<
    string,
    TokenInfo & { holders?: TokenHolder[]; error?: string }
  >;

  // Render token cards
  return (
    <div className="space-y-4">
      {Object.entries(tokenOutput).map(([address, tokenData], idx) => (
        <SingleTokenCard
          address={address}
          cardId={`${cardId}-token-${address || idx}`}
          chainId={input.chainId}
          key={address}
          onQuickAction={onQuickAction}
          tokenData={tokenData}
        />
      ))}
    </div>
  );
};

export const TokenCard = memo(PureTokenCard);

/**
 * Individual token card component
 */
type SingleTokenCardProps = {
  cardId: string;
  address: string;
  tokenData: TokenInfo & { holders?: TokenHolder[]; error?: string };
  chainId: string;
  onQuickAction: TokenCardProps["onQuickAction"];
};

function SingleTokenCard({
  cardId,
  address,
  tokenData,
  chainId,
  onQuickAction,
}: SingleTokenCardProps) {
  const [showAllHolders, setShowAllHolders] = useState(false);
  const chain = getChainInfo(chainId);

  // Handle token-level errors
  if (tokenData.error) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
        <div className="flex items-start gap-3">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50">
            <CoinsIcon className="size-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h4 className="font-medium text-amber-800 dark:text-amber-200">
              Token data unavailable
            </h4>
            <p className="mt-1 text-amber-600 text-sm dark:text-amber-400">
              {tokenData.error}
            </p>
            <div className="mt-2 font-mono text-amber-500 text-xs">
              {address}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const holders = tokenData.holders ?? [];
  const visibleHolders = showAllHolders
    ? holders
    : holders.slice(0, DEFAULT_VISIBLE_HOLDERS);
  const hasMoreHolders = holders.length > DEFAULT_VISIBLE_HOLDERS;

  // Quick actions for this token
  const quickActions: QuickAction[] = [
    {
      label: "Token history",
      icon: <HistoryIcon className="size-3" />,
      prompt: `Tell me the history of ${tokenData.symbol} (${address}). When was it launched? Any major events or news?`,
    },
    {
      label: "Price analysis",
      icon: <ChartBarIcon className="size-3" />,
      prompt: `Analyze the price and market dynamics of ${tokenData.symbol}. What affects its price? What are the trends?`,
    },
    {
      label: "Audit status",
      icon: <ShieldCheckIcon className="size-3" />,
      prompt: promptGenerators.checkTokenSafety(address, tokenData.symbol),
    },
    {
      label: "Where to trade",
      icon: <ShoppingCartIcon className="size-3" />,
      prompt: `Where can I buy or trade ${tokenData.symbol}? What DEXs or CEXs support it on ${chain.name}?`,
    },
  ];

  // Determine token type badge color
  const typeColors: Record<string, string> = {
    ERC20: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    ERC721: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
    ERC1155: "bg-pink-500/10 text-pink-600 dark:text-pink-400",
    NATIVE: "bg-green-500/10 text-green-600 dark:text-green-400",
    UNKNOWN: "bg-gray-500/10 text-gray-600 dark:text-gray-400",
  };

  // Header content (always visible when collapsed)
  const headerContent = (
    <div
      className={cn(
        "flex items-start justify-between border-b bg-background/50 p-4 pr-12",
        chain.bgGradient
      )}
    >
      <div className="flex items-start gap-4">
        {/* Token icon */}
        <div
          className="flex size-14 items-center justify-center rounded-full"
          style={{ backgroundColor: `${chain.color}20` }}
        >
          {tokenData.iconUrl ? (
            <img
              alt={tokenData.symbol}
              className="size-10 rounded-full"
              src={tokenData.iconUrl}
            />
          ) : (
            <span className="font-bold text-xl" style={{ color: chain.color }}>
              {tokenData.symbol.slice(0, 2)}
            </span>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">{tokenData.symbol}</h3>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 font-medium text-xs",
                typeColors[tokenData.type] ?? typeColors.UNKNOWN
              )}
            >
              {tokenData.type}
            </span>
          </div>
          <div className="text-muted-foreground text-sm">{tokenData.name}</div>
          <AddressDisplay
            address={address}
            chainId={chainId}
            className="mt-1"
            showCopy
            showExplorerLink
            size="sm"
          />
        </div>
      </div>

      <ChainBadge chainId={chainId} showFullName size="md" />
    </div>
  );

  return (
    <CollapsibleCard
      cardId={cardId}
      className={cn("bg-gradient-to-br", chain.bgGradient)}
      headerContent={headerContent}
    >
      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-px border-b bg-border md:grid-cols-4">
        <StatItem
          label="Total Supply"
          value={
            tokenData.totalSupply
              ? formatLargeNumber(
                  Number(tokenData.totalSupply) /
                    10 ** (tokenData.decimals ?? 18)
                )
              : "-"
          }
        />
        <StatItem
          label="Holders"
          value={
            tokenData.holdersCount
              ? Number(tokenData.holdersCount).toLocaleString()
              : "-"
          }
        />
        <StatItem
          label="Decimals"
          value={tokenData.decimals?.toString() ?? "-"}
        />
        <StatItem
          label="Price"
          value={
            tokenData.exchangeRate
              ? formatUsdValue(tokenData.exchangeRate)
              : "-"
          }
        />
      </div>

      {/* Description */}
      {tokenData.description && (
        <div className="border-b bg-background/30 p-4">
          <div className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wider">
            Description
          </div>
          <p className="text-sm">{tokenData.description}</p>
        </div>
      )}

      {/* Top holders */}
      {holders.length > 0 && (
        <div className="bg-background/30">
          <div className="flex items-center justify-between border-b bg-background/30 px-4 py-2">
            <div className="flex items-center gap-2 font-medium text-sm">
              <UsersIcon className="size-4" />
              <span>Top Holders ({holders.length})</span>
            </div>
          </div>
          <div className="divide-y">
            {visibleHolders.map((holder, idx) => (
              <HolderRow
                chainId={chainId}
                holder={holder}
                key={holder.address}
                onQuickAction={onQuickAction}
                rank={idx + 1}
                totalSupply={tokenData.totalSupply}
              />
            ))}
          </div>
          {hasMoreHolders && (
            <button
              className="w-full border-t bg-muted/30 p-2 text-center text-primary text-xs hover:bg-muted/50"
              onClick={() => setShowAllHolders(!showAllHolders)}
              type="button"
            >
              {showAllHolders
                ? "Show less"
                : `+${holders.length - DEFAULT_VISIBLE_HOLDERS} more holders`}
            </button>
          )}
        </div>
      )}

      {/* Quick actions */}
      <div className="bg-background/50 p-4">
        <QuickActionsBar
          actions={quickActions}
          onAction={onQuickAction}
          size="sm"
        />
      </div>
    </CollapsibleCard>
  );
}

/**
 * Stat item in the stats grid
 */
function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-background/30 p-3 text-center">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="font-semibold tabular-nums">{value}</div>
    </div>
  );
}

/**
 * Holder row component
 */
type HolderRowProps = {
  holder: TokenHolder;
  rank: number;
  totalSupply?: string;
  chainId: string;
  onQuickAction: TokenCardProps["onQuickAction"];
};

function HolderRow({
  holder,
  rank,
  totalSupply,
  chainId,
  onQuickAction,
}: HolderRowProps) {
  const [hovered, setHovered] = useState(false);

  // Calculate percentage if we have total supply
  const percentage =
    holder.percentage ??
    (totalSupply
      ? (Number(holder.balance) / Number(totalSupply)) * 100
      : undefined);

  return (
    <div
      className="group flex items-center gap-3 p-3 transition-colors hover:bg-muted/30"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Rank */}
      <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted font-medium text-xs">
        {rank}
      </div>

      {/* Address */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <AddressDisplay
            address={holder.address}
            chainId={chainId}
            isContract={holder.isContract}
            knownName={holder.name}
            onClick={() =>
              onQuickAction(
                promptGenerators.aboutAddress(
                  holder.address,
                  "is a top holder of this token"
                )
              )
            }
            showCopy={false}
            showExplorerLink={false}
            size="sm"
          />
          {holder.isContract && (
            <span className="rounded bg-amber-500/10 px-1 py-0.5 font-medium text-[10px] text-amber-600 dark:text-amber-400">
              Contract
            </span>
          )}
        </div>
      </div>

      {/* Percentage bar */}
      {percentage !== undefined && (
        <div className="w-24 space-y-1">
          <Progress className="h-1.5" value={Math.min(percentage, 100)} />
          <div className="text-right font-mono text-muted-foreground text-xs">
            {percentage.toFixed(2)}%
          </div>
        </div>
      )}

      {/* Quick action */}
      {hovered && (
        <QuickActionButton
          label="Who?"
          onClick={() =>
            onQuickAction(
              promptGenerators.aboutAddress(
                holder.address,
                `holds ${percentage?.toFixed(2)}% of this token`
              )
            )
          }
          showArrow
          size="sm"
          variant="subtle"
        />
      )}
    </div>
  );
}

/**
 * Skeleton loader for token card
 */
function TokenCardSkeleton({
  tokens,
  chainId,
}: {
  tokens?: string[];
  chainId: string;
}) {
  const chain = getChainInfo(chainId);
  const tokenList = tokens ?? [];

  if (tokenList.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border bg-background/30 p-6">
        <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-muted-foreground text-sm">
          Fetching token data...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {tokenList.map((token) => (
        <div
          className={cn(
            "overflow-hidden rounded-xl border bg-gradient-to-br",
            chain.bgGradient
          )}
          key={token}
        >
          {/* Header skeleton */}
          <div className="flex items-start justify-between border-b bg-background/50 p-4">
            <div className="flex items-start gap-4">
              <Skeleton className="size-14 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-40" />
              </div>
            </div>
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>

          {/* Stats skeleton */}
          <div className="grid grid-cols-4 gap-px border-b bg-border">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="bg-background/30 p-3 text-center" key={i}>
                <Skeleton className="mx-auto mb-1 h-3 w-16" />
                <Skeleton className="mx-auto h-5 w-12" />
              </div>
            ))}
          </div>

          {/* Loading indicator */}
          <div className="flex items-center justify-center gap-2 bg-background/30 p-6">
            <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-muted-foreground text-sm">
              Fetching token data...
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Error state for token card
 */
function TokenCardError({
  error,
  tokens,
  chainId,
}: {
  error: string;
  tokens: string[];
  chainId: string;
}) {
  return (
    <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-destructive/20">
          <CoinsIcon className="size-4 text-destructive" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-destructive">
            Failed to fetch token data
          </h4>
          <p className="mt-1 text-destructive/80 text-sm">{error}</p>
          {tokens.length > 0 && (
            <div className="mt-2 text-destructive/70 text-xs">
              Token: {tokens[0]}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { SingleTokenCard };
